/**
 * carrierReorderReducer — serializes carrier-order commits for a single
 * coverage type so a corrective AG Grid re-fire or a stale reset can be
 * rejected outright instead of racing a legitimate reorder (CD-1419).
 *
 * `mutationInFlight` stays true from the moment a reorder is requested until
 * the hook explicitly rearms it (see the `REARM` action) — any
 * `REORDER_REQUESTED` or `RESET` arriving in that window is rejected, closing
 * the gap that let a corrective column-state pass or a stale-proposals reset
 * silently overwrite a just-committed order.
 */

import type { CarrierSequenceOrder } from "../types/carrierSequence.types";

export type CarrierChangeSource = "drag" | "preset" | "default";

export interface PendingCarrierOrder {
  order: CarrierSequenceOrder;
  source: "drag" | "preset";
}

export interface CarrierReorderState {
  sequenceOrder: CarrierSequenceOrder;
  pendingOrder: PendingCarrierOrder | null;
  lastChangeSource: CarrierChangeSource;
  mutationInFlight: boolean;
}

export type CarrierReorderAction =
  | {
      type: "REORDER_REQUESTED";
      order: CarrierSequenceOrder;
      source: "drag" | "preset";
    }
  | { type: "COMMIT_PENDING" }
  | { type: "REVERT_PENDING" }
  | { type: "REARM" }
  | { type: "RESET"; initialOrder?: CarrierSequenceOrder };

const EMPTY_ORDER: CarrierSequenceOrder = {
  carrierIds: [],
  appliedPreset: null,
};

export const createInitialCarrierReorderState = (
  initialOrder?: CarrierSequenceOrder,
): CarrierReorderState => ({
  sequenceOrder: initialOrder?.carrierIds?.length ? initialOrder : EMPTY_ORDER,
  pendingOrder: null,
  lastChangeSource: "default",
  mutationInFlight: false,
});

export const carrierReorderReducer = (
  state: CarrierReorderState,
  action: CarrierReorderAction,
): CarrierReorderState => {
  switch (action.type) {
    case "REORDER_REQUESTED": {
      // A commit is already in flight — reject rather than let a second,
      // possibly-corrective request race the first (this is the CD-1419 fix).
      if (state.mutationInFlight) return state;
      return {
        ...state,
        pendingOrder: { order: action.order, source: action.source },
        mutationInFlight: true,
      };
    }
    case "COMMIT_PENDING": {
      if (!state.pendingOrder) return state;
      return {
        ...state,
        sequenceOrder: state.pendingOrder.order,
        lastChangeSource: state.pendingOrder.source,
        pendingOrder: null,
      };
    }
    case "REVERT_PENDING": {
      if (!state.pendingOrder) return state;
      return { ...state, pendingOrder: null, mutationInFlight: false };
    }
    case "REARM": {
      if (!state.mutationInFlight) return state;
      return { ...state, lastChangeSource: "default", mutationInFlight: false };
    }
    case "RESET": {
      // Reject a reset that lands mid-transition — otherwise a coverage-type
      // change or history-mode toggle can repaint a stale order over a
      // reorder that hasn't finished settling yet.
      if (state.mutationInFlight) return state;
      return createInitialCarrierReorderState(action.initialOrder);
    }
    default:
      return state;
  }
};
